/**
 * Karteikarten: der Lernmodus aus dem Plan.
 *
 * Das Quiz fragt mit vier Vorschlägen, und wer sie sieht, erkennt die richtige oft wieder,
 * ohne sie zu wissen. Die Karteikarte zeigt nur das Bild und fragt, was es ist; aufgedeckt
 * wird erst danach. Bewertet wird mit "gewusst" oder "nicht gewusst", und das schreibt
 * denselben Lernstand wie eine Quizantwort ohne Zeitbonus: die Wiederholung rechnet für
 * beide Wege nach SM-2, sonst stünden zwei Zahlen für dieselbe Karte.
 */
import { binnenSignale, binnenState, buoys, lightStates, signals } from './data'
import type { Lang } from './i18n'
import { pick } from './i18n'
import type { Progress } from './progress'
import type { Bereich } from './settings'
import { nextCardId, recordAnswer } from './progress'
import { describeCharacter, parseCharacter } from './rhythm'
import { binnenText, binnenZitat, resolveCite, ruleCitation, ruleText } from './sources'
import { dayNumber, type Grade } from './srs'

export type CardKind = 'state' | 'binnen' | 'buoy' | 'signal' | 'binnenSignal'

export interface CardFace {
  cardId: string
  kind: CardKind
  /** Was die Szene zeigen soll. */
  subject: string
  /** Die Antwort, aufgedeckt. */
  title: string
  explanation: string
  /** Fundstelle, wie im Katalog. */
  citation: string
  quotes: string[]
  /** Kennung eines Seezeichens, wenn es eine hat. */
  character?: string
}

/** Die nächste fällige Karte, dieselbe Reihenfolge und derselbe Bereich wie im Quiz. */
export function nextCard(progress: Progress, today = dayNumber(), bereich: Bereich = 'see'): string | undefined {
  return nextCardId(progress, today, bereich)
}

/**
 * Note für die Wiederholung. "Gewusst" ist eine 4 und nicht eine 5, weil die Karte die
 * Zeit nicht misst: die 5 vergibt das Quiz nur für eine Antwort binnen sechs Sekunden.
 * "Nicht gewusst" ist dieselbe 2 wie eine falsche Antwort.
 */
export function gradeOf(known: boolean): Grade {
  return known ? 4 : 2
}

export function recordCard(progress: Progress, cardId: string, known: boolean): Progress {
  return recordAnswer(progress, cardId, gradeOf(known))
}

/**
 * Die Karte zu einer Karten-ID, mit demselben Text und derselben Fundstelle wie der
 * Katalog. Eine zweite Textquelle gäbe es sonst, und die würde irgendwann abweichen.
 */
export function cardFace(cardId: string, lang: Lang): CardFace {
  const kind = cardId.slice(0, cardId.indexOf(':')) as CardKind
  const id = cardId.slice(cardId.indexOf(':') + 1)

  if (kind === 'buoy') {
    const buoy = buoys.find((b) => b.id === id)
    if (!buoy) throw new Error(`Seezeichen ${id} gibt es nicht`)
    const character = buoy.character ? describeCharacter(parseCharacter(buoy.character)) : undefined
    return {
      cardId,
      kind,
      subject: buoy.id,
      title: pick(buoy.name, lang),
      explanation: `${pick(buoy.meaning, lang)} ${pick(buoy.explanation, lang)}`.trim(),
      citation: buoy.cite.see ? 'SeeSchStrO Anlage I' : '33 CFR 62',
      quotes: [],
      ...(buoy.character && character
        ? { character: `${buoy.character} (${pick(character, lang)})` }
        : {}),
    }
  }

  if (kind === 'signal') {
    const signal = signals.find((s) => s.id === id)
    if (!signal) throw new Error(`Schallsignal ${id} gibt es nicht`)
    return {
      cardId,
      kind,
      subject: signal.id,
      title: pick(signal.name, lang),
      explanation: `${pick(signal.meaning, lang)} ${pick(signal.explanation, lang)}`.trim(),
      citation: ruleCitation(signal.rule, signal.cite),
      quotes: signal.cite.map((entry) =>
        ruleText(resolveCite(signal.rule, entry, signal.scope).paragraph, signal.scope === 'inland' ? 'inland' : 'international'),
      ),
    }
  }

  if (kind === 'binnenSignal') {
    const zeichen = binnenSignale.find((z) => z.id === id)
    if (!zeichen) throw new Error(`Binnen-Schallzeichen ${id} gibt es nicht`)
    return {
      cardId,
      kind,
      subject: zeichen.id,
      title: zeichen.bedeutung,
      // Wie die Lichterbilder der Ordnung nur auf Deutsch: Bedeutung und Erklärung haben
      // keine amtliche englische Fassung.
      explanation: zeichen.erklaerung,
      citation: `BinSchStrO ${zeichen.fundstelle}`,
      quotes: [`${zeichen.name.de}: „${zeichen.bedeutung}“`],
    }
  }

  if (kind === 'binnen') {
    const state = binnenState(id)
    const stellen = state.cite.map((c) => (c.includes(':') ? c : `${state.paragraph}:${c}`))
    return {
      cardId,
      kind,
      subject: state.id,
      title: pick(state.title, lang),
      // Der Normtext und die Erklärung stehen nur auf Deutsch. Eine amtliche englische
      // Fassung der Ordnung gibt es nicht, und eine selbst übersetzte wäre eine
      // Behauptung ohne Quelle.
      explanation: state.erklaerung,
      citation: stellen.map((stelle) => binnenZitat(stelle)).join(' · '),
      quotes: stellen.map((stelle) => binnenText(stelle)),
    }
  }

  const state = lightStates.find((s) => s.id === id)
  if (!state) throw new Error(`Lichterzustand ${id} gibt es nicht`)
  return {
    cardId,
    kind: 'state',
    subject: state.id,
    title: pick(state.title, lang),
    explanation: pick(state.explanation, lang),
    citation: ruleCitation(state.rule, state.cite),
    quotes: state.cite.map((entry) => ruleText(resolveCite(state.rule, entry, state.scope).paragraph)),
  }
}
